// 📄 chatService.ts
// Chat interno (v1): mensajería 1:1 y grupos, a nivel de proyecto (miembros
// de ESE proyecto) o de organización (miembros de la org activa). Un chat
// vive en `chats/{chatId}` (colección raíz, para poder consultar "mis
// chats" con una sola query `participantIds array-contains uid`) +
// subcolección `chats/{chatId}/mensajes/{id}`. Ver types/chat.ts para las
// decisiones de Fase 0 (participantIds inmutable, soft-delete de mensajes).
import {
  collection,
  doc,
  setDoc,
  updateDoc,
  query,
  where,
  orderBy,
  onSnapshot,
  serverTimestamp,
  writeBatch,
  Unsubscribe
} from "firebase/firestore";
import { db } from "../firebase";
import { Chat, ChatContexto, ChatTipo, Mensaje } from "../types/chat.ts";

// Colección "chatsEquipo" (no "chats" a secas) para no confundirse con
// usuarios/{uid}/chats, que ya existía para el historial del asistente IA
// (GeminiChat) — son 2 features completamente distintas.
const chatsRef = () => collection(db, "chatsEquipo");
const chatRef = (chatId: string) => doc(db, "chatsEquipo", chatId);
const mensajesRef = (chatId: string) => collection(db, "chatsEquipo", chatId, "mensajes");

export interface NuevoChatParams {
  tipo: ChatTipo;
  contexto: ChatContexto;
  contextoId: string;
  participantIds: string[]; // debe incluir a autorUid
  participantesInfo: Record<string, string>; // uid -> nombre/email a mostrar, ver types/chat.ts
  nombre: string; // "" si tipo=directo
  autorUid: string;
  textoPrimerMensaje: string;
}

/**
 * Crea el chat y su primer mensaje — un chat nunca existe vacío (decisión
 * de Fase 0: se crea recién al primer mensaje). A PROPÓSITO son 2 writes
 * secuenciales, NO un writeBatch: la regla de creación de un mensaje
 * necesita leer (`get()`) el doc del chat padre para validar que quien
 * escribe es participante — dentro de un mismo batch, ese `get()` no ve el
 * doc del chat todavía sin confirmar (mismo bug de timing "huevo y
 * gallina" ya documentado en otras sub-fases de este proyecto), y la regla
 * lanza un error de evaluación. Esperar a que el chat quede confirmado
 * antes de crear el mensaje lo evita — el costo es perder la atomicidad
 * entre los 2 writes, aceptable acá: en el peor caso (falla justo entre
 * medio) el chat ya queda visible en "mis chats" por `ultimoMensaje`
 * (denormalizado en el chat mismo), solo faltaría el doc del mensaje en el
 * hilo, recuperable con un reintento.
 */
export const crearChatConPrimerMensaje = async (params: NuevoChatParams): Promise<string> => {
  const texto = params.textoPrimerMensaje.trim();
  if (!texto) throw new Error("El mensaje no puede estar vacío.");
  if (params.participantIds.length < 2) throw new Error("Un chat necesita al menos 2 participantes.");
  if (params.tipo === "grupo" && !params.nombre.trim()) throw new Error("El grupo necesita un nombre.");

  const chatDocRef = doc(chatsRef());
  await setDoc(chatDocRef, {
    tipo: params.tipo,
    contexto: params.contexto,
    contextoId: params.contextoId,
    participantIds: params.participantIds,
    participantesInfo: params.participantesInfo,
    nombre: params.tipo === "grupo" ? params.nombre.trim() : "",
    creadoPor: params.autorUid,
    creadoEn: serverTimestamp(),
    ultimoMensaje: { texto, autorUid: params.autorUid, enviadoEn: serverTimestamp() },
    leidoPor: { [params.autorUid]: serverTimestamp() }
  });
  await setDoc(doc(mensajesRef(chatDocRef.id)), {
    autorUid: params.autorUid,
    texto,
    enviadoEn: serverTimestamp(),
    eliminado: false
  });

  return chatDocRef.id;
};

export const enviarMensaje = async (chatId: string, autorUid: string, texto: string): Promise<void> => {
  const limpio = texto.trim();
  if (!limpio) throw new Error("El mensaje no puede estar vacío.");

  const batch = writeBatch(db);
  batch.set(doc(mensajesRef(chatId)), {
    autorUid,
    texto: limpio,
    enviadoEn: serverTimestamp(),
    eliminado: false
  });
  batch.update(chatRef(chatId), {
    ultimoMensaje: { texto: limpio, autorUid, enviadoEn: serverTimestamp() },
    [`leidoPor.${autorUid}`]: serverTimestamp()
  });
  await batch.commit();
};

// Soft-delete: el mensaje queda como placeholder ("mensaje eliminado"),
// nunca se borra el doc — decisión de Fase 0, confirmada explícitamente
// por el usuario.
export const eliminarMensaje = async (chatId: string, mensajeId: string): Promise<void> => {
  await updateDoc(doc(mensajesRef(chatId), mensajeId), { eliminado: true });
};

export const marcarChatLeido = async (chatId: string, uid: string): Promise<void> => {
  await updateDoc(chatRef(chatId), { [`leidoPor.${uid}`]: serverTimestamp() });
};

export const subscribeMisChats = (uid: string, callback: (chats: Chat[]) => void): Unsubscribe => {
  const q = query(chatsRef(), where("participantIds", "array-contains", uid), orderBy("ultimoMensaje.enviadoEn", "desc"));
  return onSnapshot(q, (snap) => {
    callback(snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<Chat, "id">) })));
  });
};

export const subscribeMensajes = (chatId: string, callback: (mensajes: Mensaje[]) => void): Unsubscribe => {
  const q = query(mensajesRef(chatId), orderBy("enviadoEn", "asc"));
  return onSnapshot(q, (snap) => {
    callback(snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<Mensaje, "id">) })));
  });
};

// "No leído" es a nivel de CHAT, no de mensaje individual (v1, para no
// tener que llevar un contador exacto) — el chat tiene novedades si el
// último mensaje es de otra persona y llegó después de la última vez que
// este usuario lo abrió (ver marcarChatLeido).
export const tieneNoLeidos = (chat: Chat, uid: string): boolean => {
  if (chat.ultimoMensaje.autorUid === uid) return false;
  const ultimaLectura = chat.leidoPor[uid];
  if (!ultimaLectura) return true;
  const enviado = chat.ultimoMensaje.enviadoEn?.toMillis?.() ?? 0;
  const leido = ultimaLectura?.toMillis?.() ?? 0;
  return enviado > leido;
};

// Evita crear un 2do chat directo entre las mismas 2 personas en el mismo
// contexto — si ya existe, "iniciar chat" debe reabrirlo en vez de
// duplicarlo (participantIds es inmutable, así que duplicar sería
// permanente).
export const encontrarChatDirectoExistente = (
  chats: Chat[], contexto: ChatContexto, contextoId: string, uidA: string, uidB: string
): Chat | null => {
  return chats.find((c) =>
    c.tipo === "directo" &&
    c.contexto === contexto &&
    c.contextoId === contextoId &&
    c.participantIds.includes(uidA) &&
    c.participantIds.includes(uidB)
  ) || null;
};
